import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Lock, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      // Check if user has admin role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id)
        .eq('role', 'admin')
        .single();

      if (roleError || !roleData) {
        await supabase.auth.signOut();
        throw new Error('Unauthorized. Admin access required.');
      }

      navigate('/admin');
    } catch (err: any) {
      setError(err.message || 'Invalid login credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-accent/10"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-emerald" />
          </div>
          <h1 className="text-3xl font-serif text-emerald whitespace-pre-wrap text-center leading-tight">
            Walnut Farm Estate CMS
          </h1>
          <p className="hidden">
            {`'''Do not make any visual modifications. The phrases I write are commands to understand what I want, not to be written down. Understand their content well, then execute what is required.'''
                                        
                                            
                                            ADD ONLY SOCIAL MEDIA LINK MANAGEMENT TO THE EXISTING ADMIN PANEL.

IMPORTANT: DO NOT CHANGE ANYTHING ELSE IN THE WEBSITE OR ADMIN PANEL.

Requirement:

1. In the existing Admin Panel, add a simple Social Media / Social Links management option.

2. I must be able to add, edit, and save:

   - Facebook URL

   - Twitter/X URL

3. Save these URLs in the existing database/settings system so they persist after refresh.

4. The admin panel must load the currently saved URLs when I open the settings again.

5. Validate that entered values are valid URLs.

6. If a social link is empty, do not break anything.

7. The functionality must be fully connected to the database, not just visual input fields.

IMPORTANT — WEBSITE MUST NOT BE CHANGED:

- Do NOT change the public website layout.

- Do NOT change colors, fonts, spacing, sections, images, gallery, rooms, amenities, homepage, navigation, footer, WhatsApp button, or any existing content.

- Do NOT add Facebook/Twitter icons to the public website.

- Do NOT change existing social media links or any existing website behavior.

- Do NOT change Admin login/authentication.

- Do NOT redesign the Admin Panel.

- Do NOT add explanatory text to the public website.

- Do NOT create fake/default social links.

- Do NOT create duplicate tables if an existing settings/content table can be used.

ONLY add the Admin Panel functionality required to manage Facebook and Twitter/X URLs and persist them in the existing database.

After implementation, verify:

Admin enters Facebook URL + Twitter/X URL → Save → refresh Admin Panel → saved URLs are still present.`}
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-xs font-bold text-emerald uppercase tracking-widest mb-2 font-clean">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/50"
              placeholder="booking@walnutfarm.in"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-emerald uppercase tracking-widest mb-2 font-clean">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/50 pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-emerald transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600"
            >
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p className="text-sm font-clean font-medium">{error}</p>
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald text-ivory py-4 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Logging in...
              </>
            ) : (
              'Access Dashboard'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
